/** Scope picker — grouped by resource category with select all per group, plus opt-in agent secret flags */

import { AGENT_SECRET_FLAGS, SCOPE_CATEGORIES } from '@agentwiki/shared'
import { useT } from '../../i18n'

interface ScopePickerProps {
  scopes: string[]
  onChange: (scopes: string[]) => void
}

const groupHeadingClass = 'text-xs font-semibold text-fg-subtle'

function ScopeCheckbox({ scope, checked, onToggle }: { scope: string; checked: boolean; onToggle: () => void }) {
  return (
    <label className="flex min-h-8 cursor-pointer items-center gap-1.5">
      <input type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 rounded accent-accent" />
      <span className="font-mono text-xs text-fg-muted">{scope}</span>
    </label>
  )
}

export function ScopePicker({ scopes, onChange }: ScopePickerProps) {
  const t = useT()

  const toggleScope = (scope: string) => {
    onChange(scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope])
  }

  const categoryScopes = (key: string): readonly string[] => SCOPE_CATEGORIES[key as keyof typeof SCOPE_CATEGORIES].scopes

  const isCategorySelected = (key: string) => categoryScopes(key).every((s) => scopes.includes(s))

  const toggleCategory = (key: string) => {
    const catScopes = categoryScopes(key)
    onChange(isCategorySelected(key) ? scopes.filter((s) => !catScopes.includes(s)) : [...new Set([...scopes, ...catScopes])])
  }

  return (
    <div className="mt-2 space-y-3">
      {Object.entries(SCOPE_CATEGORIES).map(([key, cat]) => (
        <fieldset key={key}>
          <legend className="sr-only">{cat.label}</legend>
          <div className="mb-1 flex items-center gap-2">
            <span className={groupHeadingClass} aria-hidden="true">
              {cat.label}
            </span>
            <button type="button" onClick={() => toggleCategory(key)} className="cursor-pointer text-xs text-accent-text hover:underline">
              {isCategorySelected(key) ? t('settings.apiKeys.deselectAll') : t('settings.apiKeys.selectAll')}
            </button>
          </div>
          <div className="flex flex-wrap gap-x-3">
            {cat.scopes.map((s) => (
              <ScopeCheckbox key={s} scope={s} checked={scopes.includes(s)} onToggle={() => toggleScope(s)} />
            ))}
          </div>
        </fieldset>
      ))}

      {/* Agent flags are opt-in one by one: no "select all" and no wildcard implies them. */}
      <fieldset className="rounded-md border border-warning/30 bg-warning-soft p-2.5">
        {/* float keeps the legend inside the bordered box instead of on its edge */}
        <legend className={`float-left w-full ${groupHeadingClass}`}>{t('settings.apiKeys.agentFlags')}</legend>
        <p className="clear-left pt-0.5 text-xs text-warning">{t('settings.apiKeys.agentFlagsHint')}</p>
        <div className="mt-1 flex flex-wrap gap-x-3">
          {AGENT_SECRET_FLAGS.map((s) => (
            <ScopeCheckbox key={s} scope={s} checked={scopes.includes(s)} onToggle={() => toggleScope(s)} />
          ))}
        </div>
      </fieldset>
    </div>
  )
}
